import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectPlanningAPI } from '../api/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  DndContext, 
  DragOverlay,
  closestCorners,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  useDroppable,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { 
  Plus, 
  ArrowLeft,
  Calendar, 
  Target,
  CheckCircle2,
  Clock,
  AlertCircle,
  Trash2,
  Edit,
  Flag,
  Loader2,
  ClipboardList,
  DollarSign,
  Milestone,
  GripVertical,
  UserPlus,
  X,
  Users
} from 'lucide-react';
import { toast } from 'sonner';

const TASK_COLUMNS = [
  { id: 'todo', title: 'To Do', color: 'bg-gray-500', icon: ClipboardList },
  { id: 'in_progress', title: 'In Progress', color: 'bg-blue-500', icon: Clock },
  { id: 'completed', title: 'Completed', color: 'bg-green-500', icon: CheckCircle2 },
  { id: 'blocked', title: 'Blocked', color: 'bg-red-500', icon: AlertCircle },
];

const PRIORITY_COLORS = {
  low: 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  medium: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
  high: 'bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300',
  urgent: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
};

// Draggable Task Card Component
const DraggableTaskCard = ({ task, onEdit, onDelete, onAssign, isDragging }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: task._id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const assignees = task.assignees || [];

  return (
    <div ref={setNodeRef} style={style}>
      <Card 
        className={`bg-white dark:bg-gray-700 shadow-sm hover:shadow-md transition-all cursor-grab active:cursor-grabbing ${
          isDragging ? 'ring-2 ring-blue-500 shadow-lg' : ''
        }`}
        data-testid={`task-card-${task._id}`}
      >
        <CardContent className="p-4">
          {/* Drag Handle + Task Header */}
          <div className="flex items-start justify-between mb-2">
            <div className="flex items-start gap-2 flex-1">
              <div 
                {...attributes} 
                {...listeners}
                className="mt-1 cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <GripVertical className="h-4 w-4" />
              </div>
              <h4 className="font-medium text-gray-900 dark:text-white text-sm line-clamp-2 flex-1">
                {task.title}
              </h4>
            </div>
            <div className="flex items-center gap-1 ml-2">
              <button
                onClick={(e) => { e.stopPropagation(); onAssign(task); }}
                className="p-1 text-gray-400 hover:text-green-600 dark:hover:text-green-400"
                title="Assign workers"
              >
                <UserPlus className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onEdit(task); }}
                className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400"
              >
                <Edit className="h-3 w-3" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onDelete(task._id); }}
                className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          </div>

          {/* Description */}
          {task.description && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 line-clamp-2 ml-6">
              {task.description}
            </p>
          )}

          {/* Assigned Workers */}
          {assignees.length > 0 && (
            <div className="flex items-center gap-1 mb-3 ml-6">
              <div className="flex -space-x-2">
                {assignees.slice(0, 3).map((worker, idx) => (
                  <div
                    key={worker.id || idx}
                    className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 bg-blue-500 flex items-center justify-center overflow-hidden"
                    title={worker.name}
                  >
                    {worker.profile_image ? (
                      <img src={worker.profile_image} alt={worker.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-white text-xs font-medium">
                        {worker.name?.charAt(0)?.toUpperCase() || '?'}
                      </span>
                    )}
                  </div>
                ))}
                {assignees.length > 3 && (
                  <div className="w-6 h-6 rounded-full border-2 border-white dark:border-gray-700 bg-gray-400 flex items-center justify-center">
                    <span className="text-white text-xs">+{assignees.length - 3}</span>
                  </div>
                )}
              </div>
              <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
                {assignees.length === 1 ? assignees[0].name : `${assignees.length} assigned`}
              </span>
            </div>
          )}

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mb-3 ml-6">
              {task.tags.slice(0, 3).map((tag, idx) => (
                <span 
                  key={idx}
                  className="text-xs bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 px-2 py-0.5 rounded"
                >
                  {tag}
                </span>
              ))}
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between text-xs ml-6">
            <Badge className={PRIORITY_COLORS[task.priority]}>
              <Flag className="h-3 w-3 mr-1" />
              {task.priority}
            </Badge>
            
            {task.due_date && (
              <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {new Date(task.due_date).toLocaleDateString()}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Task Card for Drag Overlay (visual feedback while dragging)
const TaskCardOverlay = ({ task }) => {
  return (
    <Card className="bg-white dark:bg-gray-700 shadow-xl ring-2 ring-blue-500 cursor-grabbing w-[280px]">
      <CardContent className="p-4">
        <div className="flex items-start gap-2">
          <GripVertical className="h-4 w-4 text-blue-500 mt-1" />
          <h4 className="font-medium text-gray-900 dark:text-white text-sm line-clamp-2 flex-1">
            {task.title}
          </h4>
        </div>
        <div className="flex items-center justify-between text-xs mt-3 ml-6">
          <Badge className={PRIORITY_COLORS[task.priority]}>
            <Flag className="h-3 w-3 mr-1" />
            {task.priority}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );
};

// Droppable Column Component
const DroppableColumn = ({ column, tasks, children }) => {
  const Icon = column.icon;
  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
  });
  
  return (
    <div 
      ref={setNodeRef}
      className={`bg-gray-100 dark:bg-gray-800 rounded-xl p-4 min-h-[400px] transition-colors ${
        isOver ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900/20' : ''
      }`}
      data-testid={`column-${column.id}`}
    >
      {/* Column Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-3 h-3 rounded-full ${column.color}`}></div>
          <h3 className="font-semibold text-gray-900 dark:text-white">{column.title}</h3>
          <Badge variant="secondary" className="text-xs">{tasks.length}</Badge>
        </div>
      </div>

      {/* Tasks */}
      <SortableContext items={tasks.map(t => t._id)} strategy={verticalListSortingStrategy}>
        <div className="space-y-3 min-h-[200px]">
          {children}
          
          {tasks.length === 0 && (
            <div className={`text-center py-8 text-gray-400 dark:text-gray-500 border-2 border-dashed rounded-lg transition-colors ${
              isOver ? 'border-blue-400 bg-blue-100 dark:bg-blue-900/30' : 'border-gray-200 dark:border-gray-700'
            }`}>
              <Icon className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm">{isOver ? 'Drop here!' : 'Drop tasks here'}</p>
            </div>
          )}
        </div>
      </SortableContext>
    </div>
  );
};

const ProjectPlanDetail = () => {
  const { planId } = useParams();
  const navigate = useNavigate();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningTask, setAssigningTask] = useState(null);
  const [availableWorkers, setAvailableWorkers] = useState([]);
  const [selectedWorkerIds, setSelectedWorkerIds] = useState([]);
  const [loadingWorkers, setLoadingWorkers] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [taskForm, setTaskForm] = useState({
    title: '',
    description: '',
    priority: 'medium',
    due_date: '',
    estimated_hours: '',
    tags: ''
  });
  const [milestoneForm, setMilestoneForm] = useState({
    title: '',
    description: '',
    due_date: ''
  });
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('tasks');

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor)
  );

  useEffect(() => {
    fetchPlan();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [planId]);

  const fetchPlan = async () => {
    setLoading(true);
    try {
      const response = await projectPlanningAPI.getPlanById(planId);
      setPlan(response.data.plan);
    } catch (error) {
      console.error('Error fetching plan:', error);
      toast.error('Failed to load project plan');
      navigate('/project-planning');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title.trim()) {
      toast.error('Task title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: taskForm.title,
        description: taskForm.description || null,
        priority: taskForm.priority,
        due_date: taskForm.due_date || null,
        estimated_hours: taskForm.estimated_hours ? parseFloat(taskForm.estimated_hours) : null,
        tags: taskForm.tags ? taskForm.tags.split(',').map(t => t.trim()).filter(Boolean) : []
      };

      if (editingTask) {
        await projectPlanningAPI.updateTask(editingTask._id, payload);
        toast.success('Task updated!');
      } else {
        await projectPlanningAPI.createTask(planId, payload);
        toast.success('Task created!');
      }

      setShowTaskModal(false);
      setEditingTask(null);
      setTaskForm({ title: '', description: '', priority: 'medium', due_date: '', estimated_hours: '', tags: '' });
      fetchPlan();
    } catch (error) {
      console.error('Error saving task:', error);
      toast.error(error.response?.data?.detail || 'Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    try {
      await projectPlanningAPI.updateTaskStatus(taskId, newStatus);
      fetchPlan();
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;
    
    try {
      await projectPlanningAPI.deleteTask(taskId);
      toast.success('Task deleted');
      fetchPlan();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleCreateMilestone = async (e) => {
    e.preventDefault();
    if (!milestoneForm.title.trim()) {
      toast.error('Milestone title is required');
      return;
    }

    setSaving(true);
    try {
      await projectPlanningAPI.createMilestone(planId, {
        title: milestoneForm.title,
        description: milestoneForm.description || null,
        due_date: milestoneForm.due_date || null
      });
      toast.success('Milestone created!');
      setShowMilestoneModal(false);
      setMilestoneForm({ title: '', description: '', due_date: '' });
      fetchPlan();
    } catch (error) {
      console.error('Error creating milestone:', error);
      toast.error('Failed to create milestone');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleMilestone = async (milestoneId) => {
    try {
      await projectPlanningAPI.toggleMilestone(milestoneId);
      fetchPlan();
    } catch (error) {
      console.error('Error toggling milestone:', error);
      toast.error('Failed to update milestone');
    }
  };

  const handleDeleteMilestone = async (milestoneId) => {
    if (!window.confirm('Delete this milestone?')) return;
    
    try {
      await projectPlanningAPI.deleteMilestone(milestoneId);
      toast.success('Milestone deleted');
      fetchPlan();
    } catch (error) {
      console.error('Error deleting milestone:', error);
      toast.error('Failed to delete milestone');
    }
  };

  // Worker Assignment Handlers
  const openAssignModal = async (task) => {
    setAssigningTask(task);
    setSelectedWorkerIds(task.assigned_to || []);
    setShowAssignModal(true);
    
    // Fetch available workers
    setLoadingWorkers(true);
    try {
      const response = await projectPlanningAPI.getAvailableWorkers(planId);
      setAvailableWorkers(response.data.workers || []);
    } catch (error) {
      console.error('Error fetching workers:', error);
      toast.error('Failed to load available workers');
    } finally {
      setLoadingWorkers(false);
    }
  };

  const handleAssignWorkers = async () => {
    if (!assigningTask) return;
    
    setSaving(true);
    try {
      await projectPlanningAPI.assignWorkers(assigningTask._id, selectedWorkerIds);
      toast.success('Workers assigned successfully');
      setShowAssignModal(false);
      setAssigningTask(null);
      fetchPlan();
    } catch (error) {
      console.error('Error assigning workers:', error);
      toast.error(error.response?.data?.detail || 'Failed to assign workers');
    } finally {
      setSaving(false);
    }
  };

  const toggleWorkerSelection = (workerId) => {
    setSelectedWorkerIds(prev => 
      prev.includes(workerId) 
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const openEditTask = (task) => {
    setEditingTask(task);
    setTaskForm({
      title: task.title || '',
      description: task.description || '',
      priority: task.priority || 'medium',
      due_date: task.due_date ? task.due_date.split('T')[0] : '',
      estimated_hours: task.estimated_hours || '',
      tags: (task.tags || []).join(', ')
    });
    setShowTaskModal(true);
  };

  const getTasksByStatus = (status) => {
    return (plan?.tasks || []).filter(task => task.status === status);
  };

  // Find which column a task belongs to
  const findColumnForTask = (taskId) => {
    const task = plan?.tasks?.find(t => t._id === taskId);
    return task?.status || null;
  };

  // DnD Handlers
  const handleDragStart = (event) => {
    const { active } = event;
    const task = plan?.tasks?.find(t => t._id === active.id);
    setActiveTask(task);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveTask(null);

    if (!over) return;

    const taskId = active.id;
    const task = plan?.tasks?.find(t => t._id === taskId);
    
    if (!task) return;

    // Determine the target column and position
    const columnIds = TASK_COLUMNS.map(c => c.id);
    let targetStatus = null;
    let overTaskId = null;
    
    // Check if dropped over a column directly
    if (columnIds.includes(over.id)) {
      targetStatus = over.id;
    } else {
      // Dropped over another task - get its column and position
      const overTask = plan?.tasks?.find(t => t._id === over.id);
      if (overTask) {
        targetStatus = overTask.status;
        overTaskId = over.id;
      }
    }

    if (!targetStatus) return;

    const sourceStatus = task.status;
    const isSameColumn = sourceStatus === targetStatus;

    if (isSameColumn && overTaskId) {
      // REORDERING within the same column
      const columnTasks = getTasksByStatus(sourceStatus);
      const oldIndex = columnTasks.findIndex(t => t._id === taskId);
      const newIndex = columnTasks.findIndex(t => t._id === overTaskId);

      if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
        // Reorder tasks
        const reorderedTasks = arrayMove(columnTasks, oldIndex, newIndex);
        
        // Create new order values
        const taskOrders = reorderedTasks.map((t, idx) => ({
          task_id: t._id,
          order: idx
        }));

        // Optimistic UI update
        setPlan(prev => {
          const otherTasks = prev.tasks.filter(t => t.status !== sourceStatus);
          const updatedColumnTasks = reorderedTasks.map((t, idx) => ({ ...t, order: idx }));
          return {
            ...prev,
            tasks: [...otherTasks, ...updatedColumnTasks].sort((a, b) => a.order - b.order)
          };
        });

        try {
          await projectPlanningAPI.reorderTasks(taskOrders);
          toast.success('Tasks reordered');
        } catch (error) {
          console.error('Error reordering tasks:', error);
          toast.error('Failed to reorder tasks');
          fetchPlan(); // Revert on error
        }
      }
    } else if (!isSameColumn) {
      // MOVING to a different column (status change)
      // Calculate new order (add to end of target column)
      const targetColumnTasks = getTasksByStatus(targetStatus);
      const newOrder = targetColumnTasks.length;

      // Optimistic update
      setPlan(prev => ({
        ...prev,
        tasks: prev.tasks.map(t => 
          t._id === taskId ? { ...t, status: targetStatus, order: newOrder } : t
        )
      }));

      try {
        await projectPlanningAPI.updateTaskStatus(taskId, targetStatus);
        toast.success(`Task moved to ${targetStatus.replace('_', ' ')}`);
        fetchPlan(); // Refresh to get accurate stats
      } catch (error) {
        console.error('Error updating task status:', error);
        toast.error('Failed to move task');
        fetchPlan(); // Revert on error
      }
    }
  };

  const handleDragOver = (event) => {
    // This is used for real-time visual feedback during dragging
    // The actual logic is handled in handleDragEnd
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <p className="text-gray-600 dark:text-gray-400">Project plan not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate('/project-planning')}
                className="text-gray-600 dark:text-gray-400"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">{plan.title}</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {plan.job?.title} • {plan.job?.location}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={`${
                plan.phase === 'completed' ? 'bg-green-100 text-green-700' :
                plan.phase === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                'bg-blue-100 text-blue-700'
              }`}>
                {plan.phase?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
              </Badge>
            </div>
          </div>

          {/* Stats Bar */}
          <div className="flex items-center gap-6 mt-4 text-sm">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-purple-500" />
              <span className="text-gray-600 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-white">{plan.stats?.progress || 0}%</span> complete
              </span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-gray-600 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-white">{plan.stats?.completed || 0}</span>/{plan.stats?.total_tasks || 0} tasks
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Milestone className="h-4 w-4 text-blue-500" />
              <span className="text-gray-600 dark:text-gray-400">
                <span className="font-semibold text-gray-900 dark:text-white">{plan.stats?.milestones_completed || 0}</span>/{plan.stats?.milestones_total || 0} milestones
              </span>
            </div>
            {plan.budget_allocated && (
              <div className="flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-green-500" />
                <span className="text-gray-600 dark:text-gray-400">
                  ${(plan.budget_spent || 0).toLocaleString()} / ${plan.budget_allocated.toLocaleString()}
                </span>
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-4 mt-4 border-b border-gray-200 dark:border-gray-700 -mx-4 px-4">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tasks' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <ClipboardList className="h-4 w-4 inline mr-1" />
              Tasks Board
            </button>
            <button
              onClick={() => setActiveTab('milestones')}
              className={`pb-3 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'milestones' 
                  ? 'border-blue-600 text-blue-600' 
                  : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
              }`}
            >
              <Milestone className="h-4 w-4 inline mr-1" />
              Milestones ({plan.milestones?.length || 0})
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {activeTab === 'tasks' && (
          <>
            {/* Add Task Button + Drag Hint */}
            <div className="mb-6 flex items-center justify-between">
              <p className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-2">
                <GripVertical className="h-4 w-4" />
                Drag and drop tasks between columns to change status
              </p>
              <Button 
                onClick={() => {
                  setEditingTask(null);
                  setTaskForm({ title: '', description: '', priority: 'medium', due_date: '', estimated_hours: '', tags: '' });
                  setShowTaskModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-task-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Task
              </Button>
            </div>

            {/* Kanban Board with DnD */}
            <DndContext
              sensors={sensors}
              collisionDetection={closestCorners}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragOver={handleDragOver}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {TASK_COLUMNS.map((column) => {
                  const tasks = getTasksByStatus(column.id);
                  
                  return (
                    <DroppableColumn key={column.id} column={column} tasks={tasks}>
                      {tasks.map((task) => (
                        <DraggableTaskCard
                          key={task._id}
                          task={task}
                          onEdit={openEditTask}
                          onDelete={handleDeleteTask}
                          onAssign={openAssignModal}
                          isDragging={activeTask?._id === task._id}
                        />
                      ))}
                    </DroppableColumn>
                  );
                })}
              </div>

              {/* Drag Overlay */}
              <DragOverlay>
                {activeTask ? <TaskCardOverlay task={activeTask} /> : null}
              </DragOverlay>
            </DndContext>
          </>
        )}

        {activeTab === 'milestones' && (
          <>
            {/* Add Milestone Button */}
            <div className="mb-6 flex justify-end">
              <Button 
                onClick={() => setShowMilestoneModal(true)}
                className="bg-blue-600 hover:bg-blue-700"
                data-testid="add-milestone-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Milestone
              </Button>
            </div>

            {/* Milestones List */}
            <div className="space-y-4">
              {(plan.milestones || []).length === 0 ? (
                <Card className="dark:bg-gray-800">
                  <CardContent className="py-12 text-center">
                    <Milestone className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-1">No Milestones Yet</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      Add milestones to track major project phases and deliverables.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                plan.milestones.map((milestone) => (
                  <Card 
                    key={milestone._id}
                    className={`dark:bg-gray-800 ${milestone.completed ? 'bg-green-50 dark:bg-green-900/20' : ''}`}
                  >
                    <CardContent className="py-4">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <button
                            onClick={() => handleToggleMilestone(milestone._id)}
                            className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-colors ${
                              milestone.completed 
                                ? 'bg-green-500 border-green-500 text-white' 
                                : 'border-gray-300 dark:border-gray-600 hover:border-green-500'
                            }`}
                          >
                            {milestone.completed && <CheckCircle2 className="h-4 w-4" />}
                          </button>
                          <div>
                            <h4 className={`font-medium ${
                              milestone.completed 
                                ? 'text-gray-500 dark:text-gray-400 line-through' 
                                : 'text-gray-900 dark:text-white'
                            }`}>
                              {milestone.title}
                            </h4>
                            {milestone.description && (
                              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {milestone.description}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-4">
                          {milestone.due_date && (
                            <span className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1">
                              <Calendar className="h-4 w-4" />
                              {new Date(milestone.due_date).toLocaleDateString()}
                            </span>
                          )}
                          <button
                            onClick={() => handleDeleteMilestone(milestone._id)}
                            className="p-2 text-gray-400 hover:text-red-600"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </>
        )}
      </div>

      {/* Task Modal */}
      {showTaskModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
              {editingTask ? 'Edit Task' : 'Add New Task'}
            </h2>
            
            <form onSubmit={handleCreateTask} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Task Title *
                </label>
                <input
                  type="text"
                  value={taskForm.title}
                  onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })}
                  placeholder="e.g., Install electrical wiring"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="task-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={taskForm.description}
                  onChange={(e) => setTaskForm({ ...taskForm, description: e.target.value })}
                  placeholder="Task details..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Priority
                  </label>
                  <select
                    value={taskForm.priority}
                    onChange={(e) => setTaskForm({ ...taskForm, priority: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Due Date
                  </label>
                  <input
                    type="date"
                    value={taskForm.due_date}
                    onChange={(e) => setTaskForm({ ...taskForm, due_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Estimated Hours
                  </label>
                  <input
                    type="number"
                    value={taskForm.estimated_hours}
                    onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: e.target.value })}
                    placeholder="e.g., 4"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Tags (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={taskForm.tags}
                    onChange={(e) => setTaskForm({ ...taskForm, tags: e.target.value })}
                    placeholder="e.g., electrical, phase1"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowTaskModal(false);
                    setEditingTask(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={saving || !taskForm.title.trim()}
                  data-testid="submit-task-btn"
                >
                  {saving ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : editingTask ? (
                    'Update Task'
                  ) : (
                    'Add Task'
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Milestone Modal */}
      {showMilestoneModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Add Milestone</h2>
            
            <form onSubmit={handleCreateMilestone} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Milestone Title *
                </label>
                <input
                  type="text"
                  value={milestoneForm.title}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, title: e.target.value })}
                  placeholder="e.g., Foundation Complete"
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  required
                  data-testid="milestone-title-input"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Description
                </label>
                <textarea
                  value={milestoneForm.description}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, description: e.target.value })}
                  placeholder="What does this milestone represent?"
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Target Date
                </label>
                <input
                  type="date"
                  value={milestoneForm.due_date}
                  onChange={(e) => setMilestoneForm({ ...milestoneForm, due_date: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMilestoneModal(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  disabled={saving || !milestoneForm.title.trim()}
                  data-testid="submit-milestone-btn"
                >
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Add Milestone'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Worker Assignment Modal */}
      {showAssignModal && assigningTask && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Assign Workers</h2>
              <button 
                onClick={() => { setShowAssignModal(false); setAssigningTask(null); }}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Assigning to: <span className="font-medium text-gray-900 dark:text-white">{assigningTask.title}</span>
            </p>

            {loadingWorkers ? (
              <div className="flex-1 flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
              </div>
            ) : availableWorkers.length === 0 ? (
              <div className="flex-1 text-center py-8">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 dark:text-gray-400 mb-2">No workers available</p>
                <p className="text-sm text-gray-500 dark:text-gray-500">
                  Workers become available after you hire them for a job.
                </p>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto space-y-2 mb-4">
                  {availableWorkers.map((worker) => {
                    const isSelected = selectedWorkerIds.includes(worker.id);
                    return (
                      <div
                        key={worker.id}
                        onClick={() => toggleWorkerSelection(worker.id)}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          isSelected 
                            ? 'bg-blue-50 dark:bg-blue-900/30 border-2 border-blue-500' 
                            : 'bg-gray-50 dark:bg-gray-700 border-2 border-transparent hover:border-gray-300 dark:hover:border-gray-600'
                        }`}
                        data-testid={`worker-option-${worker.id}`}
                      >
                        {/* Worker Avatar */}
                        <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center overflow-hidden flex-shrink-0">
                          {worker.profile_image ? (
                            <img src={worker.profile_image} alt={worker.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white font-medium">
                              {worker.name?.charAt(0)?.toUpperCase() || '?'}
                            </span>
                          )}
                        </div>

                        {/* Worker Info */}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{worker.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                            {worker.skills?.slice(0, 2).map((skill, idx) => (
                              <span key={idx} className="bg-gray-100 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                                {skill}
                              </span>
                            ))}
                            {worker.rating > 0 && (
                              <span className="flex items-center gap-0.5">
                                ⭐ {worker.rating.toFixed(1)}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Checkbox */}
                        <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          isSelected 
                            ? 'bg-blue-500 border-blue-500' 
                            : 'border-gray-300 dark:border-gray-500'
                        }`}>
                          {isSelected && <CheckCircle2 className="h-4 w-4 text-white" />}
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Footer */}
                <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    variant="outline"
                    onClick={() => { setShowAssignModal(false); setAssigningTask(null); }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleAssignWorkers}
                    className="flex-1 bg-blue-600 hover:bg-blue-700"
                    disabled={saving}
                    data-testid="confirm-assign-btn"
                  >
                    {saving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="h-4 w-4 mr-2" />
                        Assign {selectedWorkerIds.length > 0 ? `(${selectedWorkerIds.length})` : ''}
                      </>
                    )}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectPlanDetail;
